import React from 'react';
import { withRouter } from 'react-router';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import remove from 'lodash/remove';

import IncidentStatusEnum from 'redpoints-model-js/enumeration/IncidentStatusEnum';
import MLModelTypeEnum from 'redpoints-model-js/enumeration/MLModelTypeEnum';
import AssetImageTrainingMLProjectionFilter from 'redpoints-model-js/model/filter/AssetImageTrainingMLProjectionFilter';

import HttpStatus from 'redpoints-connector-common/enumeration/HttpStatus';
import Pageable from 'redpoints-connector-common/model/pageable/Pageable';
import AssetController from 'rpc-restapi-connector/controller/AssetController';
import AssetImageController from 'redpoints-restapi-connector/controller/AssetImageController';
import AssetImageTrainingMLProjectionController from 'redpoints-restapi-connector/controller/AssetImageTrainingMLProjectionController';
import DomainPlatformsForAssetImageTrainingMLProjectionController from 'redpoints-restapi-connector/controller/DomainPlatformsForAssetImageTrainingMLProjectionController';
import IncidentStatusGroupController from 'redpoints-restapi-connector/controller/IncidentStatusGroupController';
import AssetModel from 'ml-restapi-connector/controller/AssetModelController';

import {
  Button,
  Card,
  Collapse,
  Modal,
  Notification,
  Panel,
} from 'redpoints-front-components/index';
import { buildArrayFromEnumGroups } from 'redpoints-front-common/helpers';

import ImageRecognitionFilters from './ImageRecognitionFilters';
import { translate } from '../../../helpers/languageHelper';
import ModalBulkActions from './bulkActions/ModalBulkActions';
import GridImageRecognition from './grid/GridImageRecognition';

import './imageRecognition.less';

const notificationSuccess = {
  description: translate('tasks.image_recognition.notificationSuccess'),
};
const notificationError = {
  description: translate('tasks.image_recognition.notificationError'),
};

class ImageRecognition extends React.Component {
  static getFilters(state) {
    const filters = new AssetImageTrainingMLProjectionFilter();

    Object.entries(state.filters).forEach(entry => {
      if (entry[1]) {
        if (Array.isArray(entry[1])) {
          if (entry[1].length > 0) {
            filters[entry[0]] = entry[1];
          }
        } else {
          filters[entry[0]] = parseFloat(entry[1]);
        }
      }
    });

    filters.incidentStatus = buildArrayFromEnumGroups(
      { response: { data: state.incidentStatusDataResponse } },
      state.incidentStatusSelected.incidentStatusGroup,
      state.incidentStatusSelected.incidentStatus,
      'incidentStatuses',
      'incidentStatusGroupId',
      IncidentStatusEnum
    );

    return filters;
  }

  constructor() {
    super();

    this.state = {
      resp: {
        elements: [],
        totalElements: 0,
      },
      machineLearningModelStats: {
        averagePrecision: '',
        itemsNegative: '',
        itemsPositive: '',
      },
      assetSelected: {
        key: null,
        value: null,
      },
      incidentStatusSelected: {
        incidentStatus: null,
        incidentStatusGroup: null,
      },
      assetsSelectedBulk: [],
      assetTypeData: [],
      domainPlatformData: [],
      disablePanel: true,
      reset: false,
      modalVisible: false,
      assetOpen: {
        assetId: null,
        customerId: null,
        imageId: null,
        position: null,
        src: null,
      },
      bulkModalActive: false,
      newStatusBulkAction: null,
      filters: {
        domainPlatform: [],
        minScoring: null,
        maxScoring: null,
      },
      isActionButtonDisabled: false,
      needFetchMoreData: true,
      userAction: false,
    };

    this.applyFilters = this.applyFilters.bind(this);
    this.assetMatching = this.assetMatching.bind(this);
    this.cancelBulkActions = this.cancelBulkActions.bind(this);
    this.finishBulkAction = this.finishBulkAction.bind(this);
    this.handleCheckBox = this.handleCheckBox.bind(this);
    this.handleFilter = this.handleFilter.bind(this);
    this.handleModal = this.handleModal.bind(this);
    this.handleSwitchFetchData = this.handleSwitchFetchData.bind(this);
    this.openModalBulkAction = this.openModalBulkAction.bind(this);
    this.resetFilters = this.resetFilters.bind(this);
    this.setAutoNextCard = this.setAutoNextCard.bind(this);
    this.setNewCard = this.setNewCard.bind(this);
  }

  componentDidMount() {
    this.getAssetTypeData();
    this.getincidentStatusData();
  }

  async getAssetTypeData() {
    const result = await AssetController.list(this.props.context);
    if (result.status === HttpStatus.OK.value) {
      const assetTypeData = result.response.map(element => ({
        value: element.name,
        key: element.assetId,
      }));

      this.setState({ assetTypeData });
    }
  }

  async getDomainPlatformData(assetId) {
    const result = await DomainPlatformsForAssetImageTrainingMLProjectionController.listDomainPlatforms(
      this.props.context,
      assetId,
      MLModelTypeEnum.IMAGE_RECOGNITION,
      false
    );

    if (result.status === HttpStatus.OK.value) {
      const domainPlatformData = result.response.map(element => ({
        value: element.name,
        key: element.domainPlatformId,
      }));

      this.setState({ domainPlatformData });
    }
  }

  async getincidentStatusData() {
    const result = await IncidentStatusGroupController.getIncidentStatusGroups(
      this.props.context
    );
    if (result.status === HttpStatus.OK.value) {
      const incidentStatusData = result.response
        .filter(({ incidentStatusGroupId }) => incidentStatusGroupId !== -1)
        .map(({ incidentStatusGroupId, literalKey, incidentStatuses }) => ({
          title: translate(literalKey),
          value: `incidentStatusGroup_${incidentStatusGroupId}`,
          key: `incidentStatusGroup_${incidentStatusGroupId}`,
          children: incidentStatuses.map(({ value, literalKey }) => ({
            title: translate(literalKey),
            value: `incidentStatus_${value}`,
            key: `incidentStatus_${value}`,
          })),
        }));

      this.setState({
        incidentStatusData,
        incidentStatusDataResponse: result.response,
      });
    }
  }

  static assetModelPromises = {};

  static getAssetModel = (context, assetId) => {
    if (ImageRecognition.assetModelPromises[assetId] === undefined) {
      const assetModelPromise = AssetModel.get(context, assetId);
      ImageRecognition.assetModelPromises[assetId] = assetModelPromise;
    }

    return ImageRecognition.assetModelPromises[assetId];
  };

  async getData(page, size) {
    const pagination = new Pageable();

    let averagePrecision = '';
    let itemsNegative = '';
    let itemsPositive = '';

    pagination.page = page;
    pagination.size = size;
    pagination.order = null;
    const dataArray = [];
    let plusPosition = 0;

    const filters = ImageRecognition.getFilters(this.state);

    const assetImageTrainingMLPromise = AssetImageTrainingMLProjectionController.getAssetImageTrainingMLProjection(
      this.props.context,
      this.state.assetSelected.key,
      MLModelTypeEnum.IMAGE_RECOGNITION,
      filters,
      pagination
    );
    const assetModelPromise = ImageRecognition.getAssetModel(
      this.props.context,
      this.state.assetSelected.key
    );

    const [resp, assetModelResponse] = await Promise.all([
      assetImageTrainingMLPromise,
      assetModelPromise,
    ]);

    if (assetModelResponse.status === HttpStatus.OK.value) {
      averagePrecision =
        assetModelResponse.response.accuracy != null
          ? Math.floor(assetModelResponse.response.accuracy * 100)
          : null;
      itemsNegative = assetModelResponse.response.labelednegative || null;
      itemsPositive = assetModelResponse.response.labeledpositive || null;
    }

    if (resp.status === HttpStatus.OK.value) {
      for (let i = 0; i < resp.response.content.length; i += 1) {
        if (resp.response.number === 1) {
          plusPosition = pagination.size;
        } else if (resp.response.number > 1) {
          plusPosition = resp.response.number * pagination.size;
        }
        const push = {
          customerId: resp.response.content[i].customerId,
          imageId: resp.response.content[i].imageId,
          assetId: this.state.assetSelected.key,
          position: plusPosition + i,
          scoring: resp.response.content[i].scoring,
          src: resp.response.content[i].imageURL,
        };
        dataArray.push(push);
      }
    }

    this.setState({
      resp: {
        elements: this.state.resp.elements.concat(dataArray),
        totalElements: resp.response.totalElements
          ? resp.response.totalElements
          : 0,
      },
      machineLearningModelStats: {
        averagePrecision,
        itemsNegative,
        itemsPositive,
      },
      userAction: false,
      isActionButtonDisabled: false,
    });

    if (this.state.modalVisible) {
      this.setAutoNextCard();
    }

    return dataArray;
  }

  setAutoNextCard() {
    const { assetOpen, needFetchMoreData, resp } = this.state;
    let nextCard = resp.elements[assetOpen.position];
    let prevCard = resp.elements[assetOpen.position - 2];

    if (!needFetchMoreData) {
      const assetOpenIndex = resp.elements.findIndex(
        ({ imageId }) => imageId === assetOpen.imageId
      );
      nextCard = resp.elements[assetOpenIndex + 1];
      prevCard = resp.elements[assetOpenIndex - 1];
    }

    let nextAssetOpen = {};
    if (nextCard !== undefined) {
      nextAssetOpen = nextCard;
    } else if (prevCard !== undefined) {
      nextAssetOpen = prevCard;
    } else {
      this.handleModal(false);
    }
    this.setState({ assetOpen: nextAssetOpen });
  }

  setNewCard(position) {
    const { assetOpen, resp } = this.state;
    const nextCard = resp.elements[assetOpen.position + 1];
    const prevCard = resp.elements[assetOpen.position - 1];
    let nextAssetOpen = {};

    if (position === 'next') {
      if (nextCard !== undefined) {
        nextAssetOpen = nextCard;
      } else if (prevCard !== undefined) {
        nextAssetOpen = prevCard;
      }
    } else if (position === 'prev') {
      if (prevCard !== undefined) {
        nextAssetOpen = prevCard;
      } else if (nextCard !== undefined) {
        nextAssetOpen = nextCard;
      }
    }

    this.setState({
      assetOpen: nextAssetOpen,
      nextCard: nextAssetOpen.position !== resp.elements.length,
      prevCard: nextAssetOpen.position !== 0,
      reset: false,
    });
  }

  handleFilter(filterInputValue, filterName) {
    const { filters, assetTypeData } = this.state;
    let handleFilterState = {};

    if (filterName === 'assetSelected') {
      const assetValue = assetTypeData.find(
        element => element.key === filterInputValue
      );
      handleFilterState = {
        assetSelected: {
          key: filterInputValue,
          value: assetValue.value,
        },
      };
      this.getDomainPlatformData(filterInputValue);
    } else if (filterName === 'incidentStatusSelected') {
      const output = {};

      (filterInputValue || []).forEach(value => {
        const [type, typeId] = value.split('_');
        output[type] = [...(output[type] || []), Number(typeId)];
      });

      handleFilterState = {
        incidentStatusSelected: {
          incidentStatus: output.incidentStatus,
          incidentStatusGroup: output.incidentStatusGroup,
        },
      };
    } else {
      let newFilters = { ...filters };
      newFilters[filterName] = filterInputValue;

      handleFilterState = {
        filters: newFilters,
      };
    }

    this.setState({
      ...handleFilterState,
      reset: false,
    });
  }

  applyFilters() {
    this.setState({
      needFetchMoreData: true,
      disablePanel: false,
      reset: true,
      userAction: true,
      resp: { elements: [], totalElements: 0 },
    });
  }

  resetFilters() {
    this.setState({
      assetSelected: {
        key: null,
        value: null,
      },
      incidentStatusSelected: {
        incidentStatus: null,
        incidentStatusGroup: null,
      },
      filters: {
        domainPlatform: [],
        minScoring: null,
        maxScoring: null,
      },
      disablePanel: true,
      resp: {
        elements: [],
        totalElements: 0,
      },
      machineLearningModelStats: {
        averagePrecision: '',
        itemsNegative: '',
        itemsPositive: '',
      },
      assetsSelectedBulk: [],
      reset: true,
    });
  }

  handleModal(option, assetOpen) {
    if (option) {
      const nextCard = assetOpen.position !== this.state.resp.elements.length;
      const prevCard = assetOpen.position !== 0;

      this.setState({
        modalVisible: option,
        assetOpen,
        reset: false,
        nextCard,
        prevCard,
      });
    } else {
      this.setState({
        modalVisible: option,
        reset: false,
      });
    }
  }

  async assetMatching(assetMatching, asset) {
    if (this.state.isActionButtonDisabled || isEmpty(asset)) return;

    this.setState({ isActionButtonDisabled: true });

    const newContext = cloneDeep(this.props.context);
    newContext.customerId = asset.customerId;

    const resp = await AssetImageController.setMatches(
      newContext,
      asset.assetId,
      asset.imageId,
      assetMatching
    );

    if (resp.status === HttpStatus.NO_CONTENT.value) {
      Notification.success(notificationSuccess);
      if (this.state.needFetchMoreData) {
        this.setState({
          resp: {
            elements: [],
          },
          reset: true,
          userAction: true,
        });
      } else {
        if (this.state.modalVisible) {
          this.setAutoNextCard();
        }
        const newElements = this.state.resp.elements.filter(
          ({ imageId }) => imageId !== asset.imageId
        );
        this.setState({
          newStatusBulkAction: null,
          bulkModalActive: false,
          assetsSelectedBulk: [],
          isActionButtonDisabled: false,
          resp: {
            elements: newElements,
            totalElements: newElements.length,
          },
          reset: true,
        });
      }
    } else {
      Notification.error(notificationError);
      this.setState({
        isActionButtonDisabled: false,
      });
    }
  }

  handleCheckBox(checked, asset) {
    const assetsSelectedBulk = this.state.assetsSelectedBulk;

    if (checked) {
      assetsSelectedBulk.push(asset);
    } else {
      remove(
        assetsSelectedBulk,
        currentObject => currentObject.imageId === asset.imageId
      );
    }

    this.setState({
      assetsSelectedBulk,
      reset: false,
    });
  }

  openModalBulkAction(newStatus) {
    this.setState({
      newStatusBulkAction: newStatus,
      bulkModalActive: true,
      reset: false,
    });
  }

  finishBulkAction() {
    if (this.state.needFetchMoreData) {
      this.setState({
        newStatusBulkAction: null,
        bulkModalActive: false,
        assetsSelectedBulk: [],
        resp: {
          elements: [],
        },
        machineLearningModelStats: {
          averagePrecision: '',
          itemsNegative: '',
          itemsPositive: '',
        },
        reset: true,
        userAction: true,
      });
    } else {
      const bulkedImageIds = this.state.assetsSelectedBulk.map(
        ({ imageId }) => imageId
      );
      const newElements = this.state.resp.elements.filter(
        ({ imageId }) => !bulkedImageIds.includes(imageId)
      );
      this.setState({
        newStatusBulkAction: null,
        bulkModalActive: false,
        assetsSelectedBulk: [],
        resp: {
          elements: newElements,
          totalElements: newElements.length,
        },
        reset: true,
      });
    }
  }

  cancelBulkActions() {
    this.setState({
      newStatusBulkAction: null,
      bulkModalActive: false,
    });
  }

  handleSwitchFetchData(checked) {
    if (checked) {
      this.setState({ needFetchMoreData: false });
    } else {
      this.setState({
        disablePanel: false,
        needFetchMoreData: true,
        reset: true,
        resp: { elements: [], totalElements: 0 },
        userAction: true,
      });
    }
  }

  render() {
    const { context } = this.props;
    const {
      assetOpen,
      assetSelected,
      assetTypeData,
      assetsSelectedBulk,
      bulkModalActive,
      disablePanel,
      domainPlatformData,
      needFetchMoreData,
      filters,
      incidentStatusData,
      incidentStatusSelected,
      isActionButtonDisabled,
      machineLearningModelStats,
      modalVisible,
      newStatusBulkAction,
      nextCard,
      prevCard,
      reset,
      resp,
      userAction,
    } = this.state;

    const modalClassName = `image-recognition-modal ${
      nextCard ? '' : 'disable-next-icon'
    } ${prevCard ? '' : 'disable-prev-icon'}`;

    return (
      <section className="image-recognition-page">
        <ImageRecognitionFilters
          assetSelected={assetSelected}
          assetTypeData={assetTypeData}
          disablePanel={disablePanel}
          domainPlatformData={domainPlatformData}
          fetchMoreData={needFetchMoreData}
          filters={filters}
          handleSwitchFetchData={this.handleSwitchFetchData}
          incidentStatusData={incidentStatusData}
          incidentStatusSelected={incidentStatusSelected}
          machineLearningModelStats={machineLearningModelStats}
          onApplyFilters={this.applyFilters}
          onFilterChange={this.handleFilter}
          onResetFilters={this.resetFilters}
          resp={resp}
          totalElements={resp.totalElements}
        />
        <Collapse>
          <Panel header="Bulk actions" disabled={disablePanel}>
            <div className="image-recognition-page__filters image-recognition-page__filters--flex-row">
              <div className="bulk-actions-component">
                <div className="bulk-actions-component__button">
                  <Button
                    ghost="true"
                    type="ghost"
                    onClick={() => this.openModalBulkAction(true)}
                  >
                    <CheckOutlined className="bulk-actions-component__check-section" />
                  </Button>
                </div>
                <div className="bulk-actions-component__button">
                  <Button
                    ghost="true"
                    type="ghost"
                    onClick={() => this.openModalBulkAction(false)}
                  >
                    <CloseOutlined className="bulk-actions-component__close-section" />
                  </Button>
                </div>
              </div>
              <div className="image-recognition-page__filters-selected">
                <span>
                  {assetsSelectedBulk.length}{' '}
                  {translate('tasks.image_recognition.selected')}
                </span>
              </div>
            </div>
          </Panel>
        </Collapse>
        {!disablePanel && (
          <GridImageRecognition
            context={context}
            data={resp.elements}
            getData={(page, size) => this.getData(page, size)}
            fetchMoreData={needFetchMoreData}
            handleCheckBox={this.handleCheckBox}
            handleModal={this.handleModal}
            reset={reset}
            userAction={userAction}
          />
        )}
        <Modal
          className={modalClassName}
          footer={null}
          onCancel={() => this.handleModal(false)}
          visible={modalVisible}
          width="auto"
        >
          <Card
            actionCancelButton={() => this.assetMatching(false, assetOpen)}
            actionConfirmButton={() => this.assetMatching(true, assetOpen)}
            actionNextButton={() => this.setNewCard('next')}
            actionPreviousButton={() => this.setNewCard('prev')}
            imgUrl={assetOpen.src}
            isActionButtonDisabled={isActionButtonDisabled}
            title={assetSelected.value}
          />
        </Modal>
        <ModalBulkActions
          active={bulkModalActive}
          assetId={assetSelected.key}
          assetsSelected={assetsSelectedBulk}
          context={context}
          newStatus={newStatusBulkAction}
          onCancel={this.cancelBulkActions}
          onFinishBulkAction={this.finishBulkAction}
        />
      </section>
    );
  }
}

export default withRouter(ImageRecognition);
